/**
 * GameInputManager - Central input management for Midgard
 * 
 * Handles input state isolation between:
 * - 3D game controls (movement, camera, interactions)
 * - UI/Menu systems (inventory, settings, chat)
 * 
 * When a menu is open or chat is focused:
 * - Movement controls are disabled
 * - Camera orbit is disabled
 * - Keyboard shortcuts don't trigger game actions
 * - Touch controls don't move character
 * 
 * Features:
 * - Menu stack tracking (multiple overlays)
 * - Chat focus isolation
 * - Android back button handling
 * - Keyboard shortcut filtering
 */

import { create } from 'zustand';
import { useEffect, useCallback } from 'react';

/**
 * Input State Store
 * Tracks what UI elements are capturing input
 */
export const useInputState = create((set, get) => ({
  // Menu state
  menuStack: [], // Stack of open menus ['inventory', 'settings']
  isChatFocused: false,
  isTyping: false,
  
  // Computed: should game input be disabled?
  get isInputBlocked() {
    const state = get();
    return state.menuStack.length > 0 || state.isChatFocused || state.isTyping;
  },
  
  // Push a menu onto the stack
  pushMenu: (menuId) => set((state) => ({
    menuStack: [...state.menuStack.filter(m => m !== menuId), menuId],
  })),
  
  // Pop a menu from the stack
  popMenu: (menuId) => set((state) => ({
    menuStack: state.menuStack.filter(m => m !== menuId),
  })),
  
  // Close all menus
  closeAllMenus: () => set({ menuStack: [] }),
  
  // Set chat focus
  setChatFocused: (focused) => set({ isChatFocused: focused }),
  
  // Set typing state (any input field)
  setTyping: (typing) => set({ isTyping: typing }),
  
  // Check if a specific menu is open
  isMenuOpen: (menuId) => get().menuStack.includes(menuId),
  
  // Get top menu
  getTopMenu: () => {
    const stack = get().menuStack;
    return stack.length > 0 ? stack[stack.length - 1] : null;
  },
}));

/**
 * Hook to get input blocked state
 */
export function useIsInputBlocked() {
  const menuStack = useInputState(state => state.menuStack);
  const isChatFocused = useInputState(state => state.isChatFocused);
  const isTyping = useInputState(state => state.isTyping);
  
  return menuStack.length > 0 || isChatFocused || isTyping;
}

/**
 * Hook to manage a menu's input state
 * Automatically pushes/pops from stack based on isOpen
 */
export function useMenuInputCapture(menuId, isOpen) {
  const pushMenu = useInputState(state => state.pushMenu);
  const popMenu = useInputState(state => state.popMenu);
  
  useEffect(() => {
    if (isOpen) {
      pushMenu(menuId);
    } else {
      popMenu(menuId);
    }
    
    return () => {
      popMenu(menuId);
    };
  }, [isOpen, menuId, pushMenu, popMenu]);
}

/**
 * Hook to manage chat focus state
 */
export function useChatInputCapture() {
  const setChatFocused = useInputState(state => state.setChatFocused);
  
  const onFocus = useCallback(() => setChatFocused(true), [setChatFocused]);
  const onBlur = useCallback(() => setChatFocused(false), [setChatFocused]);
  
  return { onFocus, onBlur };
}

/**
 * Hook to manage general typing state
 * Use for any input fields that should block game input
 */
export function useTypingInputCapture() {
  const setTyping = useInputState(state => state.setTyping);
  
  const onFocus = useCallback(() => setTyping(true), [setTyping]);
  const onBlur = useCallback(() => setTyping(false), [setTyping]);
  
  return { onFocus, onBlur };
}

/**
 * Hook for Android back button handling
 * Returns true if back button should close a menu, false to allow default
 */
export function useAndroidBackHandler(onBack) {
  const menuStack = useInputState(state => state.menuStack);
  const closeAllMenus = useInputState(state => state.closeAllMenus);
  const popMenu = useInputState(state => state.popMenu);
  
  useEffect(() => {
    const handlePopState = (e) => {
      // If menus are open, close top menu instead of navigating
      if (menuStack.length > 0) {
        e.preventDefault();
        const topMenu = menuStack[menuStack.length - 1];
        popMenu(topMenu);
        
        // Push state back to prevent actual navigation
        window.history.pushState(null, '', window.location.href);
        
        if (onBack) {
          onBack(topMenu);
        }
      }
    };
    
    // Push initial state
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [menuStack, popMenu, onBack]);
}

/**
 * Hook to filter keyboard events when input is blocked
 * Use this with ecctrl's keyboard controls
 */
export function useFilteredKeyboard() {
  const isInputBlocked = useIsInputBlocked();
  
  useEffect(() => {
    if (!isInputBlocked) return;
    
    // Prevent game-related keyboard events when input is blocked
    const handleKeyDown = (e) => {
      // Allow system keys
      if (e.key === 'Escape' || e.key === 'Tab') return;
      
      // Block WASD, Space, Shift, E, F, etc. when menus are open
      const blockedKeys = [
        'KeyW', 'KeyA', 'KeyS', 'KeyD',
        'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
        'Space', 'ShiftLeft', 'ShiftRight',
        'KeyE', 'KeyF', 'KeyQ', 'KeyR',
      ];
      
      if (blockedKeys.includes(e.code)) {
        e.stopPropagation();
        // Don't preventDefault for Space in text inputs
        if (e.code !== 'Space' || document.activeElement.tagName !== 'INPUT') {
          e.preventDefault();
        }
      }
    };
    
    // Capture phase to intercept before game handlers
    window.addEventListener('keydown', handleKeyDown, { capture: true });
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [isInputBlocked]);
}

/**
 * Provider component to set up input filtering
 * Wrap your game component with this
 */
export function GameInputProvider({ children }) {
  useFilteredKeyboard();
  
  return children;
}

export default useInputState;
